import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, CardHeader, CardBody, Button } from 'reactstrap';
import { useAlertPlus } from '../../../hooks';
import AlertModal from '../../../components/AlertModal';
import Form from './Form';
import config from 'config';
import { ApiService } from '../../../services';
import { getPreviewSrc, encodeFileName } from '../../../helpers/filesPreview';

import './Edit.scss';

export interface StaffRequest {
  matriculate: {
    id: string | undefined;
    nombre: string;
    apellido: string;
    delegacion: string;
  };

  director_tecnico: {
    id: string | undefined;
    nombre: string;
    apellido: string;
    delegacion: string;
  };

  calle: string;
  numero: string;
  piso: string;
  departamento: string;
  localidad_name: string;
  partido_name: string;
  codigo_postal: string;
  dias_horarios_atencion: string;

  motivo_rechazo: string;

  state_id: string;

  attachs: RequestAttachment[];
}

export interface StaffRequestAttachs {
  tipo: string;
  tipo_archivo: string;
  url: string;
}

export interface GalleryItem {
  src: string;
  downloadUrl: string;
  alt: string;
}

export interface RequestAttachment {
  id: number;
  request_id: number;
  request_type: string;
  attachment_type: string;
  url: string;
  created_at: string;
}

function Edit() {
  const { id } = useParams();

  const [data, setData] = useState<StaffRequest>({
    matriculate: {
      id: id,
      nombre: '',
      apellido: '',
      delegacion: '',
    },

    director_tecnico: {
      id: id,
      nombre: '',
      apellido: '',
      delegacion: '',
    },

    calle: '',
    numero: '',
    piso: '',
    departamento: '',
    localidad_name: '',
    partido_name: '',
    codigo_postal: '',
    dias_horarios_atencion: '',

    motivo_rechazo: '',

    state_id: '',

    attachs: [],
  });

  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const galleryContainer = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [alertShow, alertMessage, alertCallback, alert] = useAlertPlus('');

  const handleSend = async (accept = false) => {
    try {
      setIsLoading(true);

      // Preparamos los datos
      const formData = new FormData();
      formData.append('motivo_rechazo', data.motivo_rechazo ?? '');

      const headers: HeadersInit = {
        ...(localStorage.getItem('accessToken') && {
          Authorization: localStorage.getItem('accessToken') as string,
        }),
      };

      const res = await fetch(
        `${config.baseUrl}/matriculates/staff-request/${ id }/${ accept ? 'accept' : 'reject' }`,
        {
          method: 'POST',
          headers,
          body: formData,
        },
      );

      const response = await res.json();

      if (response?.status !== 'ok') {
        alert(
          response.message ??
          'Ha ocurrido un error al enviar al servidor, por favor intente nuevamente.',
        );
        return;
      }

      alert('Se guardó correctamente la resolución.', () => {
        window.location.href = '/admin/matriculates/requests';
      });
    } catch (err) {
      console.error(err);
      alert('Ha ocurrido un error al guardar la resolución, por favor intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const ATTACHMENTS_CONFIG = [
    { key: 'constancia_pagos', alt: 'Constancia de Pagos' },
    { key: 'solicitud_habilitacion', alt: 'Solicitud de Habilitación' },
    { key: 'titulos_grado', alt: 'Título de Grado' },
    { key: 'titulos_grado_dorso', alt: 'Dorso Título de Grado' },
    { key: 'constancia_cuit', alt: 'Constancia de CUIT' },
    { key: 'resolucion_ministerio', alt: 'Resolución del Ministerio' },
  ] as const;

  useEffect(() => {
    ApiService.getOne('matriculates/staff-request', id)
      .then(({ payload }) => {
        setData(payload);

        const attachs = ATTACHMENTS_CONFIG.flatMap(({ key, alt }) => {
          if (!payload.attachs?.length) return [];

          const filtered = payload.attachs
            .filter((a: RequestAttachment) => a.attachment_type.startsWith(key))
            .sort((a: RequestAttachment, b: RequestAttachment) => {
              const indexA = parseInt(a.attachment_type.split('_').pop() || '0');
              const indexB = parseInt(b.attachment_type.split('_').pop() || '0');
              return indexA - indexB;
            });

          let i = 0;
          return filtered.map((a: RequestAttachment) => {
            const encodedPath = encodeFileName(a.url);
            i++;

            return {
              src: getPreviewSrc(encodedPath),
              downloadUrl: `${config.baseUrl}/${encodedPath}`,
              // @ts-ignore
              alt: `${alt} ${i}`,
            };
          });
        });

        setGallery(attachs);
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  return (
    <>
      <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body"></div>
        </Container>
      </div>
      <Container className="mt--7" fluid>
        <Card className="bg-secondary shadow">
          <CardHeader>
            <Row>
              <Col md="8">
                <h3 className="mb-0">Solicitud de Staff #{ id }</h3>
              </Col>
              <Col md="4" className="text-right">
                <Link to='/admin/matriculates/requests'>
                  <Button color="primary" size="md">Volver</Button>
                </Link>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Form
              data={ data }
              setData={ setData }
              gallery={ gallery }
              galleryContainer={ galleryContainer }
              isLoading={ isLoading }
              onAccept={ () => handleSend(true) }
              onReject={ () => handleSend(false) }
            />
          </CardBody>
        </Card>
      </Container>
      <AlertModal isOpen={ alertShow } message={ alertMessage } onClose={ alertCallback } />
    </>
  );
}

export default Edit;
